// Disabled due to the function parameters from the API are not typed.
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  HttpClient,
  HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpUrlEncodingCodec,
} from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { BASE_PATH as DEP_BASE_PATH, Configuration as DepConfiguration } from '@rocksoft/mdm-dep-sdk';
import { BASE_PATH, Configuration, Token } from '@rocksoft/mdm-sdk-v2';
import { Observable } from 'rxjs';

@Injectable()
export class HttpRequestService {
  protected basePath = 'https://localhost';
  protected depBasePath = 'https://localhost';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();
  public depConfiguration = new DepConfiguration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() @Inject(DEP_BASE_PATH) depBasePath: string,
    @Optional() configuration: Configuration,
    @Optional() depConfiguration: DepConfiguration
  ) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
    if (depBasePath) {
      this.depBasePath = depBasePath;
    }
    if (depConfiguration) {
      this.depConfiguration = depConfiguration;
      this.depBasePath = depBasePath || depConfiguration.basePath || this.depBasePath;
    }
  }

  /**
   * Create a new CSR to be uploaded to Apple.
   *
   * @param apiVersion The requested API version
   * @param forceNewRequest Optional flag to force any current CSR operations to be cancelled if true.
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public startPushCertificateRequest(
    apiVersion: string,
    forceNewRequest?: boolean,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Blob>;
  public startPushCertificateRequest(
    apiVersion: string,
    forceNewRequest?: boolean,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Blob>>;
  public startPushCertificateRequest(
    apiVersion: string,
    forceNewRequest?: boolean,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Blob>>;
  public startPushCertificateRequest(
    apiVersion: string,
    forceNewRequest?: boolean,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    return HttpRequestService.sendRequest(
      {
        accepts: ['application/octet-stream', 'application/x-plist'],
        configuration: this.configuration,
        defaultHeaders: this.defaultHeaders,
        httpClient: this.httpClient,
        method: 'post',
        path: `${this.basePath}/certificates/push/start`,
        addQueryParameters: (queryParams: HttpParams) => {
          if (forceNewRequest !== undefined && forceNewRequest !== null) {
            queryParams = queryParams.set('forceNewRequest', forceNewRequest);
          } else {
            queryParams = queryParams.set('forceNewRequest', false);
          }
        },
      },
      apiVersion,
      observe,
      reportProgress
    );
  }

  /**
   * Get the dep certificate for creating an MDM Server.
   *
   * @param apiVersion The requested API version
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public getPemRequest(apiVersion: string, observe?: 'body', reportProgress?: boolean): Observable<Blob>;
  public getPemRequest(
    apiVersion: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Blob>>;
  public getPemRequest(apiVersion: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<Blob>>;
  public getPemRequest(apiVersion: string, observe: any = 'body', reportProgress = false): Observable<any> {
    return HttpRequestService.sendRequest(
      {
        accepts: ['application/octet-stream', 'application/x-pem-file'],
        configuration: this.depConfiguration,
        defaultHeaders: this.defaultHeaders,
        httpClient: this.httpClient,
        method: 'get',
        path: `${this.depBasePath}/certificates`,
      },
      apiVersion,
      observe,
      reportProgress
    );
  }

  /**
   * Create a new vendor CSR that will be used to create a new vendor certificate.
   *
   * @param apiVersion The requested API version
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public startVendorCertificateRequest(
    apiVersion: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Blob>;
  public startVendorCertificateRequest(
    apiVersion: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Blob>>;
  public startVendorCertificateRequest(
    apiVersion: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Blob>>;
  public startVendorCertificateRequest(
    apiVersion: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    return HttpRequestService.sendRequest(
      {
        accepts: ['application/octet-stream'],
        configuration: this.configuration,
        defaultHeaders: this.defaultHeaders,
        httpClient: this.httpClient,
        method: 'get',
        path: `${this.basePath}/certificates/vendor/start`,
      },
      apiVersion,
      observe,
      reportProgress
    );
  }

  /**
   * Download current MDM vendor certificate
   *
   * @param apiVersion The requested API version
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public downloadCer(apiVersion: string, observe?: 'body', reportProgress?: boolean): Observable<Blob>;
  public downloadCer(
    apiVersion: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Blob>>;
  public downloadCer(apiVersion: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<Blob>>;
  public downloadCer(apiVersion: string, observe: any = 'body', reportProgress = false): Observable<any> {
    return HttpRequestService.sendRequest(
      {
        accepts: ['application/octet-stream'],
        configuration: this.configuration,
        defaultHeaders: this.defaultHeaders,
        httpClient: this.httpClient,
        method: 'get',
        path: `${this.basePath}/certificates/vendor/cer`,
      },
      apiVersion,
      observe,
      reportProgress
    );
  }

  /**
   * Upload a new MDM vendor certificate
   *
   * @param apiVersion The requested API version
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public completeVendorCertificate(
    apiVersion: string,
    certificate: string | ArrayBuffer,
    observe?: 'body' | 'response' | 'events',
    reportProgress?: boolean
  ): Observable<Token>;
  public completeVendorCertificate(
    apiVersion: string,
    certificate: string | ArrayBuffer,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    const body = new FormData();
    body.append('certificate', new Blob([certificate]), 'vendor.pfx');

    return HttpRequestService.sendRequest(
      {
        accepts: ['application/json'],
        body,
        configuration: this.configuration,
        defaultHeaders: this.defaultHeaders,
        httpClient: this.httpClient,
        method: 'post',
        path: `${this.basePath}/certificates/vendor/complete`,
      },
      apiVersion,
      observe,
      reportProgress
    );
  }

  private static sendRequest(
    req: RocksoftRequest,
    apiVersion: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Blob>;
  private static sendRequest(
    req: RocksoftRequest,
    apiVersion: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Blob>>;
  private static sendRequest(
    req: RocksoftRequest,
    apiVersion: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Blob>>;
  private static sendRequest(
    {
      method,
      path,
      configuration,
      accepts,
      defaultHeaders,
      addQueryParameters,
      httpClient,
      body,
      addHeaders,
      responseType,
    }: RocksoftRequest,
    apiVersion: string,
    observe: 'body' | 'events' | 'response' = 'body',
    reportProgress = false
  ): Observable<any> {
    const queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    queryParameters.set('api-version', apiVersion);
    if (addQueryParameters !== undefined) {
      addQueryParameters(queryParameters);
    }

    let headers = defaultHeaders;
    if (configuration.accessToken) {
      const accessToken =
        typeof configuration.accessToken === 'function' ? configuration.accessToken() : configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    const httpHeaderAcceptSelected: string | undefined = configuration.selectHeaderAccept(accepts);

    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    if (addHeaders !== undefined) {
      addHeaders(headers);
    }

    return httpClient.request(method, path, {
      body: body,
      params: queryParameters,
      withCredentials: configuration.withCredentials,
      headers,
      observe,
      reportProgress,
      responseType: responseType,
    });
  }
}

interface RocksoftRequest {
  method: string;
  path;
  configuration: Configuration | DepConfiguration;
  accepts: string[];
  addQueryParameters?: (queryParams: HttpParams) => void;
  addHeaders?: (queryParams: HttpHeaders) => void;
  defaultHeaders: HttpHeaders;
  httpClient: HttpClient;
  body?: any;
  responseType?: 'blob' | 'json' | 'arraybuffer' | 'text';
}

class CustomHttpUrlEncodingCodec extends HttpUrlEncodingCodec {
  encodeKey(k: string): string {
    k = super.encodeKey(k);
    return k.replace(/\+/gi, '%2B');
  }
  encodeValue(v: string): string {
    v = super.encodeValue(v);
    return v.replace(/\+/gi, '%2B');
  }
}
